import React, { useState, useEffect } from "react";
import ReusableTable from "../../components/Employee/Table";
import {
  confirmDeletePopup,
  formatDateOnly,
  getEmployeePublicMediaBase64,
  getMediaBase64,
  successPopup,
} from "../../util/common";
import TextInput from "../../components/Reusable/TextInput";
import SelectInput from "../../components/Reusable/SelectInput";
import DateInput from "../../components/Reusable/DateInput";
import { useNavigate } from "react-router-dom";
import { useGetAllDepartments } from "../../hooks/useDepartments";
import {
  useDeleteEmployee,
  useGetAllEmployeesPaginated,
} from "../../hooks/useUsers";
import { FaEdit, FaTrash } from "react-icons/fa";
import Swal from "sweetalert2";

function EmployeeListLayout() {
  const [pageCount, setPageCount] = useState(1);
  const navigate = useNavigate();

  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
  });

  const [filter, setFilter] = useState({
    filterField1: "FirstName",
    filterValue1: null,
    filterField2: "Department",
    filterValue2: null,
  });

  const handleOnPageChange = (pageIndex, pageSize) => {
    setPagination({
      page: pageIndex + 1,
      pageSize,
    });
  };

  const [departmentRecords, setDepartmentRecords] = useState([]);
  const [employeeRecords, setEmployeeRecords] = useState([]);
  const departments = useGetAllDepartments();
  const employees = useGetAllEmployeesPaginated();
  const delEmployee = useDeleteEmployee();

  const fetchDepartmentRecords = async () => {
    departments.mutate(
      {},
      {
        onSuccess: async (result) => {
          if (!result.success) return;

          const records = result.data.map((r) => ({
            id: r.id,
            name: r.Name,
          }));
          setDepartmentRecords(records);
        },
      }
    );
  };

  const fetchEmployeeRecords = async () => {
    employees.mutate(
      {
        page: pagination.page,
        pageSize: pagination.pageSize,
        filterField1: filter.filterField1,
        filterValue1: filter.filterValue1,
        filterField2: filter.filterField2,
        filterValue2: filter.filterValue2,
      },
      {
        onSuccess: async (result) => {
          if (!result.success) return;

          const records = await Promise.all(
            result.data.data.map(async (record) => {
              const employeeCurrentDepartment = record.DepartmentRecords?.find(
                (d) => d.Current
              );
              return {
                ID: record?.id,
                EmployeeCode: record?.EmployeeCode,
                FullName: record?.FullName,
                Email: record?.Email,
                ContactNumber: record?.ContactNumber,
                Address: `${record?.Address}, ${record?.City}, ${record?.Province}, ${record?.Country}`,
                DateHired: formatDateOnly(record?.DateHired),
                EmploymentStatus: record?.EmploymentStatus?.Name,
                Media: await getEmployeePublicMediaBase64(
                  record?.ProfilePictureMedia?.Filename,
                  record?.id
                ),
                Department: employeeCurrentDepartment?.Department?.Name,
              };
            })
          );
          setEmployeeRecords(records);
          setPageCount(result.data.pagination.totalPages);
        },
      }
    );
  };

  useEffect(() => {
    fetchDepartmentRecords();
  }, []);

  useEffect(() => {
    fetchEmployeeRecords();
  }, [pagination, filter]);

  const handleEdit = (employee) => {
    navigate(`/EditUser/${employee.ID}`);
  };

  const handleDelete = async (employee) => {
    const isConfirmed = await confirmDeletePopup({
      text: `Are you sure you want to delete ${employee.FullName}?`,
    });

    if (isConfirmed) {
      delEmployee.mutate(employee.ID, {
        onSuccess: (data) => {
          fetchEmployeeRecords();
          successPopup({ text: "Employee has been deleted." });
        },
      });
    }
  };

  const tableColumns = [
    { accessorKey: "EmployeeCode", header: "Code" },
    {
      accessorKey: "FullName",
      header: "Employee Full Name",
      cell: ({ row }) => {
        const media = row.original.Media;

        return (
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <img
              src={media}
              alt="Profile"
              style={{
                width: "45px",
                height: "45px",
                borderRadius: "50%",
                objectFit: "cover",
              }}
              onError={(e) => (e.target.style.display = "none")}
            />
            <span>{row.original.FullName}</span>
          </div>
        );
      },
    },
    { accessorKey: "Email", header: "Email" },
    { accessorKey: "ContactNumber", header: "Contact #" },
    { accessorKey: "Address", header: "Address" },
    { accessorKey: "DateHired", header: "Date Hired" },
    { accessorKey: "EmploymentStatus", header: "Employment Status" },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const employee = row.original;

        return (
          <div style={{ display: "flex", gap: "10px" }}>
            <FaEdit
              title="Edit"
              style={{ cursor: "pointer" }}
              onClick={() => handleEdit(employee)}
            />

            <FaTrash
              title="Delete"
              style={{ cursor: "pointer", color: "red" }}
              onClick={() => handleDelete(employee)}
            />
          </div>
        );
      },
    },
  ];

  return (
    <div className="overtime-form-container">
      <h2>Employees List</h2>
      <form className="overtime-form">
        <div className="form-column">
          <TextInput
            label="Employee Name"
            placeholder={"Juan Dela Cruz"}
            onChange={(val) => {
              setFilter((prev) => ({
                ...prev,
                filterValue1: val,
              }));
            }}
            value={filter.filterValue1}
          />
        </div>

        <div className="form-column">
          <SelectInput
            label="Department"
            choices={departmentRecords}
            onChange={(val) => {
              setFilter((prev) => ({
                ...prev,
                filterValue2: val,
              }));
            }}
            value={filter.filterValue2}
          />
        </div>
        <div className="form-group full-width file-upload-section">
          <div className="attendance-filters">
            <div className="action-btns">
              <button
                className="overtime-btn"
                onClick={() => navigate("/AddUser")}
              >
                + Add Employee
              </button>
              <button
                className="overtime-btn"
                onClick={() => navigate("/AddUser")}
              >
                + Import Employees
              </button>
              <button className="overtime-btn" onClick={() => navigate("/")}>
                Generate Self Onboarding Link
              </button>
            </div>
            <div className="attendance-records">
              <ReusableTable
                data={employeeRecords}
                columns={tableColumns}
                mode="server"
                initialPageSize={pagination.pageSize}
                onPageChange={handleOnPageChange}
                pageCount={pageCount}
                page={pagination.page - 1}
                pageSize={pagination.pageSize}
              />
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

export default EmployeeListLayout;
